import { useMutation, useQueryClient } from '@tanstack/react-query';
import ReferrerService, {
  IUpdateReferrerParams,
} from '@clickersphere/core/services/ReferrerService';
import { LeadQueryData, LEADS_QUERY_KEY } from '../lead/useLeads';
import { useBuildQueryKey } from '../useBuildQueryKey';
import { REFERRERS_QUERY_KEY, ReferrerQueryData } from './useReferrers';

export function useUpdateReferrer() {
  const queryClient = useQueryClient();
  const { buildQueryKey } = useBuildQueryKey();

  const {
    isPending: updateReferrerIsPending,
    mutateAsync: updateReferrerAsync,
  } = useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: IUpdateReferrerParams;
    }) => ReferrerService.update(id, { ...data }),
    onMutate: async (variables) => {
      await queryClient.cancelQueries({
        queryKey: buildQueryKey(REFERRERS_QUERY_KEY),
      });
      await queryClient.cancelQueries({
        queryKey: buildQueryKey(LEADS_QUERY_KEY),
      });

      const oldReferrers = queryClient.getQueryData<ReferrerQueryData[]>(
        buildQueryKey(REFERRERS_QUERY_KEY),
      );
      const oldLeadsQueries = queryClient.getQueriesData<unknown>({
        queryKey: buildQueryKey(LEADS_QUERY_KEY),
      });

      queryClient.setQueryData<ReferrerQueryData[]>(
        buildQueryKey(REFERRERS_QUERY_KEY),
        (old) =>
          old?.map((referrer) =>
            referrer.id === variables.id
              ? { ...referrer, ...variables.data, queryStatus: 'pending' }
              : referrer,
          ),
      );

      if (variables.data.name) {
        const renameLead = (lead: LeadQueryData) =>
          lead.referrerId === variables.id
            ? { ...lead, referrerName: variables.data.name }
            : lead;

        oldLeadsQueries.forEach(([queryKey, data]) => {
          if (Array.isArray(data)) {
            queryClient.setQueryData(
              queryKey,
              (data as LeadQueryData[]).map(renameLead),
            );
          } else if (data && typeof data === 'object') {
            queryClient.setQueryData(
              queryKey,
              renameLead(data as LeadQueryData),
            );
          }
        });
      }

      return { oldReferrers, oldLeadsQueries };
    },

    onSuccess: async (_data, variables) => {
      await queryClient.cancelQueries({
        queryKey: buildQueryKey(REFERRERS_QUERY_KEY),
      });

      queryClient.setQueryData<ReferrerQueryData[]>(
        buildQueryKey(REFERRERS_QUERY_KEY),
        (old) =>
          old?.map((referrer) =>
            referrer.id === variables.id
              ? { ...referrer, queryStatus: undefined }
              : referrer,
          ),
      );
    },

    onError: async (_error, _variables, context) => {
      await queryClient.cancelQueries({
        queryKey: buildQueryKey(REFERRERS_QUERY_KEY),
      });
      await queryClient.cancelQueries({
        queryKey: buildQueryKey(LEADS_QUERY_KEY),
      });

      queryClient.setQueryData(
        buildQueryKey(REFERRERS_QUERY_KEY),
        context?.oldReferrers,
      );
      context?.oldLeadsQueries.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
    },
  });

  return {
    updateReferrerAsync,
    updateReferrerIsPending,
  };
}
